import { useContext, useMemo } from 'react';
import { ThemeContext } from './ThemeContext';
import type { ThemeContextValue } from '@/types/theme';

/**
 * useTheme - Custom hook to access theme context
 *
 * @returns Theme context value
 * @throws Error if used outside ThemeProvider
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * useThemeToggle - Custom hook for theme toggle functionality
 *
 * @returns Object with isDarkMode and toggleTheme
 */
export const useThemeToggle = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  return useMemo(
    () => ({
      isDarkMode,
      toggleTheme,
    }),
    [isDarkMode, toggleTheme]
  );
};
